import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PrezzoConvenzionato } from "@/components/accordi/PrezzoConvenzionato";
import { QuestionarioEnte } from "@/components/accordi/QuestionarioEnte";
import { DocumentiDo, docDoRichiesti } from "@/components/accordi/DocumentiDo";
import { toast } from "sonner";
import { AlertTriangle, BadgeCheck, Info, Loader2 } from "lucide-react";

const Campo = ({ c, value, onChange }) => (
  <div className="space-y-1.5">
    <Label>{c.label}</Label>
    {c.type === "select" ? (
      <select value={value ?? ""} onChange={(e) => onChange(typeof c.default === "number" ? Number(e.target.value) : e.target.value)} data-testid={`ente-campo-${c.key}`} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
        {c.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    ) : (
      <Input type={c.type === "number" ? "number" : "text"} min={c.min} value={value ?? ""} onChange={(e) => onChange(c.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value.toUpperCase())} data-testid={`ente-campo-${c.key}`} />
    )}
    {c.help && <p className="text-xs text-muted-foreground">{c.help}</p>}
  </div>
);

const NotaPremio = ({ res }) => (
  res.definitivo
    ? <p className="flex gap-1.5 rounded-lg bg-emerald-50 p-2.5 text-xs text-emerald-800" data-testid="ente-premio-definitivo"><BadgeCheck className="h-4 w-4 shrink-0" />Premio definitivo: resta solo la verifica del diritto all'accordo prima della proposta.</p>
    : <p className="flex gap-1.5 rounded-lg bg-amber-50 p-2.5 text-xs text-amber-900" data-testid="ente-premio-indicativo"><Info className="h-4 w-4 shrink-0" />{res.direzione ? "Premio indicativo: la Direzione Tecnica lo conferma dopo l'esame di bilanci e statuto." : "Premio indicativo: compila il questionario assuntivo per renderlo definitivo."}</p>
);

// Prodotti per l'ente aderente: form sui motori ufficiali, sconto accordo, questionario per RC e D&O.
export const CalcEnte = ({ codice, product, onSalva, onConsulenza }) => {
  const [campi, setCampi] = useState(null);
  const [f, setF] = useState({});
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRes(null); setCampi(null);
    api.post("/public/accordi/campi", { codice, product }).then(({ data }) => {
      setCampi(data); setF({ ...Object.fromEntries(data.campi.map((c) => [c.key, c.default])), ...(data.questionario.length ? { questionario: {} } : {}) });
    }).catch((e) => toast.error(apiError(e)));
  }, [codice, product]);

  const calcola = async () => {
    setBusy(true);
    try { const { data } = await api.post("/public/accordi/calcola", { codice, product, input: f }); setRes(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const set = (k, v) => { setF({ ...f, [k]: v }); setRes(null); };

  if (!campi) return <Loader2 className="h-5 w-5 animate-spin text-forest" />;
  const obbligatorio = product === "d_o";
  const docsOk = !obbligatorio || docDoRichiesti(f.tipo_ente_do).every((t) => f.allegati?.[t]);
  return (
    <div className="grid gap-5 lg:grid-cols-5" data-testid={`calc-ente-${product}`}>
      <div className="space-y-3 lg:col-span-3">
        {campi.ente && <p className="flex gap-2 rounded-lg bg-sage/50 p-3 text-xs text-forest"><Info className="h-4 w-4 shrink-0" />Soluzione per l'ente aderente (ETS, APS, ODV, Fondazione, Associazione, iscritti o non iscritti al RUNTS).</p>}
        <div className="grid gap-3 sm:grid-cols-2">{campi.campi.map((c) => <Campo key={c.key} c={c} value={f[c.key]} onChange={(v) => set(c.key, v)} />)}</div>
        {campi.questionario.length > 0 && <QuestionarioEnte domande={campi.questionario} value={f.questionario || {}} onChange={(q) => set("questionario", q)} obbligatorio={obbligatorio} />}
        {obbligatorio && <DocumentiDo codice={codice} tipoEnte={f.tipo_ente_do} value={f.allegati || {}} onChange={(t, id) => setF((prev) => ({ ...prev, allegati: { ...(prev.allegati || {}), [t]: id } }))} />}
        <Button onClick={calcola} disabled={busy} data-testid="ente-calcola" className="bg-forest hover:bg-forest-hover">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Calcola il premio</Button>
      </div>
      <div className="space-y-3 self-start lg:sticky lg:top-6 lg:col-span-2" data-testid="ente-risultato">
        {res?.ok && <>
          <PrezzoConvenzionato r={res} testid="ente-prezzo" />
          <NotaPremio res={res} />
          <ul className="space-y-0.5 text-xs text-muted-foreground">{res.garanzie.map((g) => <li key={g}>• {g}</li>)}</ul>
          {res.avvisi?.length > 0 && <ul className="space-y-0.5 text-xs text-amber-800" data-testid="ente-avvisi">{res.avvisi.map((a) => <li key={a}>• {a}</li>)}</ul>}
          {obbligatorio && !docsOk && <p className="text-xs text-amber-800" data-testid="do-documenti-mancanti">Carica statuto e {f.tipo_ente_do === "ets" ? "rendiconto" : "bilanci"} per salvare il preventivo.</p>}
          <Button onClick={() => onSalva(f, campi)} disabled={!docsOk} data-testid="ente-salva" className="w-full bg-terracotta hover:bg-terracotta-hover">Salva il preventivo</Button>
        </>}
        {res && !res.ok && (
          <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="ente-consulenza-richiesta">
            <div className="flex gap-2"><AlertTriangle className="h-4 w-4 shrink-0" />{res.errore}</div>
            {res.avvisi?.length > 0 && <ul className="text-xs">{res.avvisi.map((a) => <li key={a}>• {a}</li>)}</ul>}
            <Button variant="outline" onClick={onConsulenza} data-testid="ente-richiedi-consulenza" className="w-full">Richiedi una consulenza dedicata</Button>
          </div>
        )}
      </div>
    </div>
  );
};
